/**
 * Internal mutation functions for direct table operations.
 *
 * These are low-level mutations that operate directly on database tables.
 * They are intended for service-layer use only, not for UI components.
 *
 * @internal
 * @module fehlzeiten/services/internalMutations
 */

import { supabase } from "../../../../lib/supabase";
import type { ServiceResult } from "../types/services";
import { normalizeTime } from "./helpers";

// ============================================================================
// Absence Note Mutations
// ============================================================================

/**
 * Updates a student absence note directly.
 *
 * @internal This is for service-layer use only
 * @param noteId - Absence note UUID
 * @param updates - Fields to update
 * @returns Service result
 */
export async function updateAbsenceNote(
  noteId: string,
  updates: {
    absenceStatus?: string;
    startTime?: string | null;
    endTime?: string | null;
    reason?: string | null;
  },
): Promise<ServiceResult> {
  const payload: Record<string, unknown> = {};

  if (updates.absenceStatus !== undefined) {
    payload.absence_status = updates.absenceStatus;
  }
  if (updates.startTime !== undefined) {
    payload.start_time = normalizeTime(updates.startTime);
  }
  if (updates.endTime !== undefined) {
    payload.end_time = normalizeTime(updates.endTime);
  }
  if (updates.reason !== undefined) {
    payload.reason = updates.reason;
  }

  const { error } = await supabase
    .from("student_absence_notes" as never)
    .update(payload as never)
    .eq("id" as never, noteId as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Hard-deletes an absence note.
 *
 * @internal This is for service-layer use only
 * @param noteId - Absence note UUID
 * @returns Service result
 */
export async function softDeleteAbsenceNote(
  noteId: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_absence_notes" as never)
    .delete()
    .eq("id" as never, noteId as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Creates a new absence note directly.
 *
 * @internal This is for service-layer use only
 * @param params - Note creation parameters
 * @returns Service result with the new note ID
 */
export async function createAbsenceNote(params: {
  studentId: string;
  schoolId: string;
  startDate: string;
  endDate?: string;
  absenceStatus: string;
  reason?: string;
  startTime?: string;
  endTime?: string;
  createdBy?: string;
}): Promise<ServiceResult<{ noteId: string }>> {
  const insertData: Record<string, unknown> = {
    student_id: params.studentId,
    school_id: params.schoolId,
    start_date: params.startDate,
    end_date: params.endDate || params.startDate,
    absence_status: params.absenceStatus,
    absence_type: "single",
    submitted_via: "admin_eingabe",
    reason: params.reason || null,
  };

  if (params.startTime) {
    insertData.start_time = params.startTime;
  }
  if (params.endTime) {
    insertData.end_time = params.endTime;
  }
  if (params.createdBy) {
    insertData.created_by = params.createdBy;
  }

  const { data, error } = await supabase
    .from("student_absence_notes" as never)
    .insert(insertData as never)
    .select("id")
    .single();

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true, data: { noteId: (data as { id: string }).id } };
}

/**
 * Clears approval state on an absence note (for undo operations).
 *
 * @param noteId - Absence note UUID
 * @param schoolId - Optional school ID for additional filtering
 * @returns Service result
 */
export async function clearNoteApproval(
  noteId: string,
  schoolId?: string,
): Promise<ServiceResult> {
  const payload = {
    approval_state: null,
    approval_action: null,
    approved_by: null,
    approved_at: null,
  };

  let query = supabase
    .from("student_absence_notes" as never)
    .update(payload as never)
    .eq("id" as never, noteId as never);

  if (schoolId) {
    query = query.eq("school_id" as never, schoolId as never);
  }

  const { error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Updates lateness entries JSONB field on an absence note.
 *
 * @param noteId - Absence note UUID
 * @param entries - Updated lateness entries array
 * @param schoolId - Optional school ID for additional filtering
 * @returns Service result
 */
export async function updateLatenessEntries(
  noteId: string,
  entries: unknown[],
  schoolId?: string,
): Promise<ServiceResult> {
  const payload = {
    lateness_entries: entries,
    updated_at: new Date().toISOString(),
  };

  let query = supabase
    .from("student_absence_notes" as never)
    .update(payload as never)
    .eq("id" as never, noteId as never);

  if (schoolId) {
    query = query.eq("school_id" as never, schoolId as never);
  }

  const { error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Creates an absence note from a daily log entry.
 *
 * @param params - Creation parameters
 * @returns Service result with the new note ID
 */
export async function createNoteFromDailyLog(params: {
  studentId: string;
  schoolId: string;
  date: string;
  presenceStatus: string;
  notes?: string;
  createdBy?: string;
}): Promise<ServiceResult<{ noteId: string }>> {
  const absenceStatus =
    params.presenceStatus === "absent_excused"
      ? "krankgemeldet"
      : "unentschuldigt";

  const insertPayload: Record<string, unknown> = {
    student_id: params.studentId,
    school_id: params.schoolId,
    start_date: params.date,
    end_date: params.date,
    absence_status: absenceStatus,
    reason: params.notes || null,
  };

  if (params.createdBy) {
    insertPayload.created_by = params.createdBy;
  }

  const { data, error } = await supabase
    .from("student_absence_notes" as never)
    .insert(insertPayload as never)
    .select("id")
    .single();

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true, data: { noteId: (data as { id: string }).id } };
}

// ============================================================================
// Daily Log Mutations
// ============================================================================

/**
 * Links a daily log to an absence note.
 *
 * @internal This is for service-layer use only
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @param noteId - Absence note UUID to link
 * @returns Service result
 */
export async function linkDailyLogToNote(
  studentId: string,
  date: string,
  noteId: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({ absence_note_id: noteId } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Unlinks a daily log from an absence note by setting absence_note_id to NULL.
 *
 * @internal This is for service-layer use only
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @returns Service result
 */
export async function unlinkDailyLogFromNote(
  studentId: string,
  date: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({ absence_note_id: null } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Updates daily log notes field.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @param notes - Notes text
 * @returns Service result
 */
export async function updateDailyLogNotes(
  studentId: string,
  date: string,
  notes: string | null,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({
      notes: notes,
      updated_at: new Date().toISOString(),
    } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Merge Operations
// ============================================================================

/**
 * Parameters for merging absence notes.
 */
export interface MergeAbsenceNotesParams {
  noteIds: string[];
  keepNoteId: string;
  mergedStartTime: string | null;
  mergedEndTime: string | null;
  mergedReason: string | null;
}

/**
 * Merges multiple absence notes into one.
 *
 * Uses the fn_merge_absence_notes RPC for atomic transaction.
 *
 * @param params - Merge parameters
 * @returns Service result
 */
export async function mergeAbsenceNotes(
  params: MergeAbsenceNotesParams,
): Promise<ServiceResult> {
  const { noteIds, keepNoteId, mergedStartTime, mergedEndTime, mergedReason } =
    params;

  const { data, error } = await supabase.rpc("fn_merge_absence_notes", {
    p_note_ids: noteIds,
    p_keep_note_id: keepNoteId,
    p_merged_start_time: mergedStartTime || null,
    p_merged_end_time: mergedEndTime || null,
    p_merged_reason: mergedReason || null,
    p_merged_by: null,
  });

  if (error) {
    console.error("[mergeAbsenceNotes] RPC error:", error);
    return { success: false, error: error.message };
  }

  const result = data as { success: boolean; error?: string };
  if (!result.success) {
    return { success: false, error: result.error || "Merge failed" };
  }

  return { success: true };
}

// ============================================================================
// Attendance Status Flips
// ============================================================================

/**
 * Flips absent_unexcused AND absent_unclear attendance logs and daily
 * log to absent_excused for a given student on a specific date.
 *
 * Used by the "Doch entschuldigt" flow to convert any unresolved
 * absent state → excused. With the absent_unclear epic the
 * starting state can be either pre-klärung (absent_unclear) or
 * post-klärung formal (absent_unexcused); both should flip.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @param schoolId - School UUID for RLS filtering
 * @returns Service result
 */
export async function flipAttendanceToExcused(
  studentId: string,
  date: string,
  schoolId: string,
): Promise<ServiceResult> {
  // 1. Flip attendance logs: absent_unexcused / absent_unclear → absent_excused
  const { error: logError } = await supabase
    .from("student_attendance_logs" as never)
    .update({ status: "absent_excused" } as never)
    .eq("student_id" as never, studentId as never)
    .eq("school_id" as never, schoolId as never)
    .gte("timestamp" as never, `${date}T00:00:00` as never)
    .lt("timestamp" as never, `${date}T23:59:59.999` as never)
    .in("status" as never, [
      "absent_unexcused",
      "absent_unclear",
    ] as never);

  if (logError) {
    console.error("[flipAttendanceToExcused] attendance_logs error:", logError);
    return { success: false, error: logError.message };
  }

  // 2. Flip daily log: absent_unexcused / absent_unclear → absent_excused
  const { error: dailyError } = await supabase
    .from("student_daily_log" as never)
    .update({ presence_status: "absent_excused" } as never)
    .eq("student_id" as never, studentId as never)
    .eq("school_id" as never, schoolId as never)
    .eq("date" as never, date as never)
    .in("presence_status" as never, [
      "absent_unexcused",
      "absent_unclear",
    ] as never);

  if (dailyError) {
    console.error("[flipAttendanceToExcused] daily_log error:", dailyError);
    return { success: false, error: dailyError.message };
  }

  return { success: true };
}
